import type { AdtagEnv } from "@sphtech/web2-ads";
import { DISABLE_ADS } from "@src/app/constants";

const PUBLICATION: string | undefined =
  import.meta.env.VITE_PUBLICATION || undefined;

/**
 * Publications cut over from the legacy SPHMGAM engine (src/js/gam.js) to the
 * @sphtech/web2-ads SDK (WEBCR-1557). Titles not listed here keep the legacy
 * engine untouched. The `import.meta.env` comparison must stay first (and a
 * direct comparison, not an array lookup): Vite replaces it with a literal, so
 * for other titles the whole expression folds to `false && …` and the SDK
 * drops from their bundles without needing `DISABLE_ADS` to fold too.
 */

export const SDK_ADS_ENABLED =
  import.meta.env.VITE_PUBLICATION === "hbs" && !DISABLE_ADS;

/** SDK registry lookup key — magazine titles are namespaced `mag_<pub>`. */
export const SDK_ADS_SITE_KEY = PUBLICATION ? `mag_${PUBLICATION}` : "";

/**
 * hbs adtag-script overrides passed to `AdsProvider`. These deliberately go
 * through the provider's `SphAdsScriptsConfig` props rather than
 * `configureAdsRuntime`: only the provider layer honours a `null`/`false`
 * `prebidUrl` as "skip prebid" (the runtime-config layer types `prebidUrl` as
 * `string` and merges with `??`, so a `null` there is dropped and the registry
 * default still loads). `configUrl`/`runtimeUrl` point at the no-prebid init +
 * runtime bundles, authored as absolute *prod* URLs: the SDK's `applyAdtagEnv`
 * only rewrites a host that is exactly `adtag.sphdigital.com`, so a prod URL
 * plus `env` (SDK_ADS_ENV) yields `adtag-stg` on dev/stg and stays `adtag` on
 * prod. A path-only value would throw in `new URL()` and load from the page
 * origin instead, so full URLs are required. Keyed off the same `=== "hbs"`
 * literal as SDK_ADS_ENABLED so Vite folds these to `undefined` (registry
 * default) for every other title.
 */
export const SDK_ADS_PREBID_URL: string | null | undefined =
  import.meta.env.VITE_PUBLICATION === "hbs" ? null : undefined;
export const SDK_ADS_CONFIG_URL: string | undefined =
  import.meta.env.VITE_PUBLICATION === "hbs"
    ? "https://adtag.sphdigital.com/pub/init.js"
    : undefined;
export const SDK_ADS_RUNTIME_URL: string | undefined =
  import.meta.env.VITE_PUBLICATION === "hbs"
    ? "https://adtag.sphdigital.com/pub/nopb-runtime.js"
    : undefined;

const PUBLIC_BASE_URL: string | undefined = import.meta.env
  .VITE_PUBLIC_BASE_URL;

/**
 * Base-URL-derived adtag environment (web2-dev/web2-stg hosts → dev/stg).
 * Parses the hostname via `new URL()` so substrings in the path/query can't
 * accidentally route to the wrong adtag env.
 * Falls back to `dev` under the Vite dev server (`import.meta.env.DEV`) so a
 * local build with no/unknown base URL never loads prod adtag/GPT config; only
 * a real deployed build (DEV=false) with an unrecognised host lands on `prod`.
 */
function resolveAdtagEnv(baseUrl?: string): AdtagEnv {
  if (baseUrl) {
    try {
      const { hostname } = new URL(baseUrl);
      if (hostname.includes("web2-dev")) return "dev";
      if (hostname.includes("web2-stg")) return "stg";
    } catch {
      // Invalid URL — fall through to DEV fallback.
    }
  }
  return import.meta.env.DEV ? "dev" : "prod";
}

export const SDK_ADS_ENV: AdtagEnv = resolveAdtagEnv(PUBLIC_BASE_URL);
